import React from 'react';
import { PitchDeckSlide, PitchDeck } from '../../types/pitchDeck';

interface SlidePreviewProps {
  slide: PitchDeckSlide;
  theme: PitchDeck['theme'];
}

const SlidePreview: React.FC<SlidePreviewProps> = ({ slide, theme }) => {
  // Use slide background color or theme primary color as fallback
  const backgroundColor = slide.style.backgroundColor || theme.primaryColor;
  const textColor = slide.style.textColor || '#FFFFFF';
  
  // Parse content for bullet points (lines starting with - or *)
  const parseContent = (content: string) => {
    if (!content) return [];
    
    return content.split('\n').map((line, index) => {
      const trimmed = line.trim();
      
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        return (
          <li key={index} className="mb-2">
            {trimmed.substring(2)}
          </li>
        );
      }
      
      // If it's not a bullet point, render as paragraph
      return trimmed ? (
        <p key={index} className="mb-2">
          {trimmed}
        </p>
      ) : null;
    });
  };
  
  // Render different slide layouts based on slide type and layout style
  const renderSlideContent = () => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center p-10">
            <h1 className="text-4xl font-bold mb-6">{slide.title}</h1>
            <div className="text-xl">{slide.content}</div>
          </div>
        );
        
      case 'content':
        if (slide.style.layout === 'split' && slide.imageUrl) {
          return (
            <div className="flex h-full">
              <div className="w-1/2 p-10">
                <h2 className="text-3xl font-bold mb-6">{slide.title}</h2>
                <div className="text-lg">
                  {slide.content.includes('\n') || slide.content.includes('- ') || slide.content.includes('* ') ? (
                    <ul className="list-disc pl-5 space-y-2">{parseContent(slide.content)}</ul>
                  ) : (
                    slide.content
                  )}
                </div>
              </div>
              <div 
                className="w-1/2 bg-cover bg-center"
                style={{ backgroundImage: `url(${slide.imageUrl})` }}
              />
            </div>
          );
        } else {
          return (
            <div className={`p-10 ${slide.style.layout === 'centered' ? 'text-center' : ''}`}>
              <h2 className="text-3xl font-bold mb-6">{slide.title}</h2>
              <div className="text-lg">
                {slide.content.includes('\n') || slide.content.includes('- ') || slide.content.includes('* ') ? (
                  <ul className={`${slide.style.layout === 'centered' ? '' : 'list-disc pl-5'} space-y-2`}>
                    {parseContent(slide.content)}
                  </ul>
                ) : (
                  slide.content
                )}
              </div>
            </div>
          );
        }
        
      case 'image':
        return (
          <div className="flex flex-col h-full">
            <h2 className="text-3xl font-bold p-6 text-center">{slide.title}</h2>
            {slide.imageUrl ? (
              <div 
                className="flex-1 bg-contain bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${slide.imageUrl})` }}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-100">
                <p className="text-gray-400">No image provided</p>
              </div>
            )}
            {slide.content && (
              <div className="p-6 text-center">
                <p>{slide.content}</p>
              </div>
            )}
          </div>
        );
        
      case 'quote':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center p-10">
            <h2 className="text-2xl font-medium mb-8">{slide.title}</h2>
            <blockquote className="text-3xl italic font-light mb-6">
              "{slide.content}"
            </blockquote>
            {slide.style.attribution && (
              <div className="text-lg">— {slide.style.attribution}</div>
            )}
          </div>
        );
        
      case 'chart':
        return (
          <div className="flex flex-col h-full">
            <h2 className="text-3xl font-bold p-6 text-center">{slide.title}</h2>
            {slide.imageUrl ? (
              <div 
                className="flex-1 bg-contain bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${slide.imageUrl})` }}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-100">
                <p className="text-gray-400">Chart visualization placeholder</p>
              </div>
            )}
            {slide.content && (
              <div className="p-6">
                <p>{slide.content}</p>
              </div>
            )}
          </div>
        );
        
      case 'team':
        return (
          <div className="h-full p-10">
            <h2 className="text-3xl font-bold mb-6 text-center">{slide.title}</h2>
            
            <div className="grid grid-cols-2 gap-6 mt-10">
              {slide.style.teamMembers?.map((member, index) => (
                <div key={index} className="flex items-center">
                  <div 
                    className="w-20 h-20 rounded-full bg-gray-200 flex-shrink-0"
                    style={{ 
                      backgroundImage: member.photoUrl ? `url(${member.photoUrl})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }}
                  />
                  <div className="ml-4">
                    <h3 className="font-bold text-lg">{member.name}</h3>
                    <p className="text-sm">{member.role}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {slide.content && (
              <div className="mt-6 text-center">
                <p>{slide.content}</p>
              </div>
            )}
          </div>
        );
        
      default:
        return (
          <div className="p-10">
            <h2 className="text-3xl font-bold mb-6">{slide.title}</h2>
            <p>{slide.content}</p>
          </div>
        );
    }
  };
  
  return (
    <div 
      className="w-full aspect-[16/9] shadow-lg overflow-hidden rounded-lg"
      style={{ 
        backgroundColor, 
        color: textColor
      }}
    >
      {renderSlideContent()}
    </div>
  );
};

export default SlidePreview;

// Named export is now used instead of default export
